import { apiService } from './api';
import { CRICKET_MODEL_BASE_URL } from '../config/api';

// Types matching the Java DTOs
export interface FirstInningsRequest {
  teamARuns: number;
  ballsBowledA: number;
  aWkts: number;
}

export interface SecondInningsRequest {
  teamARuns: number;
  ballsBowledA: number;
  aWkts: number;
  teamBRuns: number;
  ballsBowledB: number;
  bWkts: number;
}

export interface WinProbabilityResponse {
  teamAProbability: number;
  teamBProbability: number;
}

export interface OverUnderLineRequest {
  ballsBowled: number;
  runsSoFar: number;
  wktsSoFar: number;
}

export interface SecondInningsOURequest {
  ballsBowled: number;
  runsSoFar: number;
  wktsSoFar: number;
}

export interface SecondInningsBBattingOURequest {
  ballsBowledB: number;
  runsB: number;
  wktsB: number;
  target: number;
}

export interface OverUnderLineResponse {
  line: number;
  overProbability: number;
}

export interface NextBallOutcomeRequest {
  innings: number;
  currentBall: number;
  wkts: number;
  runs: number;
  target?: number;
}

export interface NextBallOutcomeResponse {
  value: Record<string, Record<string, number>>;
  durationMillis: number;
}

export interface FullModelRequest {
  ballsBowledA: number;
  runsA: number;
  wktsA: number;
  ballsBowledB: number;
  runsB: number;
  wktsB: number;
  aFoursSoFar: number;
  aSixesSoFar: number;
  bFoursSoFar: number;
  bSixesSoFar: number;
}

export interface ModelProbability2Way {
  teamAProbability: number;
  teamBProbability: number;
}

export interface ModelProbability3Way {
  teamAProbability: number;
  drawProbability: number;
  teamBProbability: number;
}

export interface ModelOULine {
  line: number;
  overProbability: number;
  underProbability: number;
}

export interface ModelOddEven {
  oddProbability: number;
  evenProbability: number;
}

export interface CricketModelOutput {
  twoWay: ModelProbability2Way;
  threeWay: ModelProbability3Way;
  teamATotalRuns: ModelOULine;
  teamBTotalRuns: ModelOULine;
  matchTotalRuns: ModelOULine;
  teamATotal4s: ModelOULine;
  teamBTotal4s: ModelOULine;
  matchTotal4s: ModelOULine;
  teamATotal6s: ModelOULine;
  teamBTotal6s: ModelOULine;
  matchTotal6s: ModelOULine;
  matchTotalWickets: ModelOULine;
  teamATotalRunsOddEven: ModelOddEven;
  teamBTotalRunsOddEven: ModelOddEven;
  matchTotalRunsOddEven: ModelOddEven;
}

export interface FullModelResponse {
  value: CricketModelOutput;
  durationMillis: number;
}

// Service for interacting with the cricket model
export const cricketModelService = {
  // First innings win probability
  getFirstInningWinProbability: (request: FirstInningsRequest): Promise<WinProbabilityResponse> => {
    return fetch(`${CRICKET_MODEL_BASE_URL}/match-winner/first-innings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get first inning win probability');
      }
      return response.json();
    });
  },

  // Second innings win probability
  getSecondInningWinProbability: (request: SecondInningsRequest): Promise<WinProbabilityResponse> => {
    return fetch(`${CRICKET_MODEL_BASE_URL}/match-winner/second-innings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get second inning win probability');
      }
      return response.json();
    });
  },

  // Over/Under line for first innings
  suggestOULineFirst: (request: OverUnderLineRequest): Promise<OverUnderLineResponse> => {
    return fetch(`${CRICKET_MODEL_BASE_URL}/over-under/first-innings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get over/under line for first innings');
      }
      return response.json();
    });
  },

  // Over/Under line for second innings while A is batting
  suggestOULineSecondGivenAState: (request: SecondInningsOURequest): Promise<OverUnderLineResponse> => {
    return fetch(`${CRICKET_MODEL_BASE_URL}/over-under/second-innings-a-batting`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get over/under line for second innings (A batting)');
      }
      return response.json();
    });
  },

  // Over/Under line for second innings when B is batting
  suggestOULineSecondWhenBBatting: (request: SecondInningsBBattingOURequest): Promise<OverUnderLineResponse> => {
    return fetch(`${CRICKET_MODEL_BASE_URL}/over-under/second-innings-b-batting`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get over/under line for second innings (B batting)');
      }
      return response.json();
    });
  },

  // Full model
  getFullModel: (request: FullModelRequest): Promise<FullModelResponse> => {
    return fetch(`${CRICKET_MODEL_BASE_URL}/full-model`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get full model');
      }
      return response.json();
    });
  },

  // Next ball outcome percentages
  getNextBallOutcomePercentages: (request: NextBallOutcomeRequest): Promise<NextBallOutcomeResponse> => {
    return fetch(`${CRICKET_MODEL_BASE_URL}/next-ball-outcome`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get next ball outcome percentages');
      }
      return response.json();
    });
  },
};
